import { useState } from "react";
import clsx from "clsx";
import { Button, Textarea } from "@chef/components";
import { intl } from "./SatisfactionFeedback.Intl";

export type OnSatisfactionFeedbackSubmitted = (data: {
  value?: SatisfactionValue;
  comment?: string;
}) => void;

interface FeedbackProps {
  onSubmit: OnSatisfactionFeedbackSubmitted;
  loading?: boolean;
}

enum SatisfactionValue {
  AWFUL = 1,
  BAD = 2,
  OKAY = 3,
  GOOD = 4,
  GREAT = 5,
}

export const SatisfactionFeedback = ({ onSubmit, loading }: FeedbackProps) => {
  const [comment, setComment] = useState("");
  const [selectedSatisfactionValue, setSelectedSatisfactionValue] =
    useState<SatisfactionValue>();

  const selectedSatisfactionOption = SATISFACTION_OPTIONS.find(
    (option) => option.value === selectedSatisfactionValue,
  );

  return (
    <form
      className="flex flex-col items-stretch self-stretch gap-4 p-4 mt-8 border rounded-sm bg-background border-grey-3"
      onSubmit={(e) => {
        e.preventDefault();

        onSubmit({ value: selectedSatisfactionValue, comment });
      }}
    >
      <p className="text-center">
        <strong>{intl.RATE_YOUR_EXPERIENCE}</strong>
      </p>

      <div className="flex justify-around">
        {SATISFACTION_OPTIONS.map((option) => {
          return (
            <SatisfactionOption
              key={option.value}
              option={option}
              highlightState={getHighlightStateForOption({
                satisfactionValue: selectedSatisfactionValue,
                currentOption: option,
              })}
              onClick={() => setSelectedSatisfactionValue(option.value)}
            />
          );
        })}
      </div>

      {selectedSatisfactionOption && (
        <Textarea
          rows={5}
          id="feedback-input"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          label={selectedSatisfactionOption.feedbackPlaceholder}
          maxLength={100}
        />
      )}

      <Button primary loading={loading} type="submit" id="submit-btn">
        {intl.SHOW_MY_MENU}
      </Button>

      <p className="text-sm text-center">
        <strong>{intl.AND_PICK_YOUR_DISHES}</strong>
      </p>
    </form>
  );
};

const getHighlightStateForOption = ({
  satisfactionValue,
  currentOption,
}: {
  satisfactionValue: SatisfactionValue | undefined;
  currentOption: SatisfactionOption;
}) => {
  switch (satisfactionValue) {
    case undefined:
      return "none-selected";
    case currentOption.value:
      return "selected";
    default:
      return "other-selected";
  }
};

const SatisfactionOption = ({
  option: { emoji, label },
  highlightState,
  onClick,
}: {
  option: SatisfactionOption;
  highlightState: ReturnType<typeof getHighlightStateForOption>;
  onClick: () => void;
}) => (
  <button
    type="button"
    id={label}
    className={clsx(
      "flex flex-col items-center cursor-pointer",
      highlightState === "other-selected" && "opacity-50 hover:opacity-100",
    )}
    onClick={onClick}
  >
    <div
      className={clsx(
        "w-12 h-12 flex items-center justify-center rounded-lg relative active:bg-highlight",
        highlightState === "selected" && "bg-highlight",
        highlightState !== "other-selected" && "hover:bg-highlight",
      )}
    >
      <p className="text-3xl mt-0.5 font-none">{emoji}</p>
    </div>

    <p className="mt-1 text-xxs text-grey-1">{label}</p>
  </button>
);

const SATISFACTION_OPTIONS = [
  {
    emoji: "😣",
    label: intl.AWFUL,
    value: SatisfactionValue.AWFUL,
    feedbackPlaceholder: intl.WHAT_CAN_WE_DO_BETTER,
  },
  {
    emoji: "🙁",
    label: intl.BAD,
    value: SatisfactionValue.BAD,
    feedbackPlaceholder: intl.WHAT_CAN_WE_DO_BETTER,
  },
  {
    emoji: "😐",
    label: intl.OKAY,
    value: SatisfactionValue.OKAY,
    feedbackPlaceholder: intl.WHAT_CAN_WE_DO_BETTER,
  },
  {
    emoji: "🙂",
    label: intl.GOOD,
    value: SatisfactionValue.GOOD,
    feedbackPlaceholder: intl.WHAT_CAN_WE_DO_BETTER,
  },
  {
    emoji: "😄",
    label: intl.GREAT,
    value: SatisfactionValue.GREAT,
    feedbackPlaceholder: intl.WHAT_DID_WE_DO_SO_GOOD,
  },
] as const;
type SatisfactionOption = (typeof SATISFACTION_OPTIONS)[number];
